import { FormDrawer, FormDrawerContent, Id, LaravelResponse, isSuccess, useToasts } from "@dgs/core";
import { AxiosResponse } from "axios";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { AttachUsersToEventRequest, EventUserFormValues } from "../../types/user";
import { EventUserMultiSelect } from "./EventUserMultiSelect";
import { UserResponsibilityByEventSelect } from "./UserResponsibilitySelect";

const initialValues: EventUserFormValues = {
	users: [],
	userResponsibilities: [],
};

interface Props {
	eventId: Id;
	reloadList: () => void;
	attachUsers: (values: AttachUsersToEventRequest) => Promise<AxiosResponse<LaravelResponse<void>>>;
	open: boolean;
	handleClose: () => void;
}

export const EventUserAttachAction: FC<Props> = ({ eventId, reloadList, attachUsers, open, handleClose }) => {
	const { t } = useTranslation();
	const { showToast } = useToasts();
	const handleSubmit = useCallback(
		async (values: EventUserFormValues) => {
			const response = await attachUsers({
				userIds: values.users.map(({ id }) => id),
				userResponsibilityIds: values.userResponsibilities.map(({ id }) => id),
			});

			if (isSuccess(response)) {
				showToast({ type: "success", body: t("Users were successfully attached."), title: t("Attached users") });
				reloadList();
				return true;
			}

			return false;
		},
		[reloadList, showToast, t, attachUsers],
	);

	const validate = useCallback(
		(values: EventUserFormValues) => {
			const errors: { [K in keyof EventUserFormValues]?: any } = {};

			if (values.users.length === 0) {
				errors.users = t("Required field");
			}
			if (values.userResponsibilities.length === 0) {
				errors.userResponsibilities = t("Required field");
			}
			return errors;
		},
		[t],
	);

	return (
		<FormDrawer
			open={open}
			validate={validate}
			onSubmit={handleSubmit}
			initialValues={initialValues}
			onClose={handleClose}
			title={t("Attach users")}
			renderForm={(props) => (
				<FormDrawerContent {...props}>
					<EventUserMultiSelect eventId={eventId} name="users" label={t("New users")} />
					<UserResponsibilityByEventSelect
						eventId={eventId}
						label={t("Responsibility")}
						name="userResponsibilities"
						required={true}
					/>
				</FormDrawerContent>
			)}
		/>
	);
};
